import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import { DateTime } from 'luxon';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';
import * as color from 'smg/color';
import { IsAxiosError } from 'smg/errors';
import * as template from 'smg/template';
import * as util from 'smg/util';
import i18n from '../i18n';
import { AnimationController, MoneyAnimation } from './leaderboard-animations';

// salesTeam stats and the response wrapper are generated from Go
// (models2.SalesTeamStats / models2.SalesTeamLeaderboardResponse); members are
// real models2.User instances. `row` below is the frontend-only view-model that
// layers on computed position/ratio/change fields.
declare interface row {
    position: number;
    positionChange: number;
    todayPositionChange: number;
    quarterPositionChange: number;
    salesTeamID: string;
    originalSalesTeamID: string;
    salesTeamName: string;
    todayTotal: Big;
    pendingTotal: Big;
    lastWorkingDayTotal: Big;
    ratio: Big;
    members: models.User[];
    primaryRealmColor: string;
    quarterPoints: number;
    wins: number;
    seconds: number;
    thirds: number;
    podiums: number;
    quarterPosition: number;
}

// Store previous data for comparison
let previousData: models.SalesTeamStats[] | null = null;
let currentGridElement: HTMLElement | null = null;
const animationController = new AnimationController();
let isAnimating = false; // Prevent updates while animating
let sortMode: 'today' | 'quarter' | 'provisional' = 'today'; // Current sort mode
let currentQuarterData: models.SalesTeamLeaderboardResponse | null = null; // Store full response for re-sorting

// Sort rows based on the specified mode
function sortRows(rows: row[], mode: 'today' | 'quarter' | 'provisional'): row[] {
    let sorted: row[];

    if (mode === 'quarter') {
        // Sort by quarter points, then wins, then name
        sorted = rows.sort((a, b) => {
            if (a.quarterPoints !== b.quarterPoints) {
                return b.quarterPoints - a.quarterPoints; // Descending
            }
            if (a.wins !== b.wins) {
                return b.wins - a.wins; // More wins wins
            }
            return a.salesTeamName.localeCompare(b.salesTeamName); // Alphabetical tiebreaker
        });

        // Assign positions based on sorted order
        sorted.forEach((row, index) => {
            row.position = index + 1;
        });
    } else if (mode === 'provisional') {
        // Sort by provisional total (today + pending, highest to lowest) with quarter points as secondary sort
        sorted = rows.sort((a, b) => {
            const aProvisional = a.todayTotal.add(a.pendingTotal);
            const bProvisional = b.todayTotal.add(b.pendingTotal);

            if (aProvisional.gt(bProvisional)) {
                return -1;
            }
            if (bProvisional.gt(aProvisional)) {
                return 1;
            }
            if (a.quarterPoints !== b.quarterPoints) {
                return b.quarterPoints - a.quarterPoints; // Descending
            }
            return a.salesTeamName.localeCompare(b.salesTeamName);
        });

        sorted.forEach((row, index) => {
            row.position = index + 1;
        });
    } else {
        // Sort by today's total (highest to lowest) with quarter points as secondary sort
        sorted = rows.sort((a, b) => {
            if (a.todayTotal.gt(b.todayTotal)) {
                return -1;
            }
            if (b.todayTotal.gt(a.todayTotal)) {
                return 1;
            }
            if (a.quarterPoints !== b.quarterPoints) {
                return b.quarterPoints - a.quarterPoints; // Descending
            }
            return a.salesTeamName.localeCompare(b.salesTeamName);
        });

        sorted.forEach((row, index) => {
            row.position = index + 1;
        });
    }

    return sorted;
}

export async function SalesTeamLeaderboard(parentElement: HTMLElement, forceRefresh = false) {
    // Show loading state immediately if this is the first load
    const existingCard = parentElement.querySelector('.sales-team-leaderboard-card');
    if (!existingCard) {
        const loadingCard = template.Clone('#sales-team-leaderboard-loading').firstElementChild as HTMLElement;
        parentElement.appendChild(loadingCard);
    }

    // If this is a manual refresh, show spinning icon
    if (forceRefresh && existingCard) {
        const refreshBtn = existingCard.querySelector('.refresh-btn i') as HTMLElement;
        if (refreshBtn) {
            refreshBtn.classList.add('fa-spin');
        }
    }

    return axios.get('/v2/stats/salesteam-leaderboard').then(d => models.SalesTeamLeaderboardResponse.fromJSON(d.data)).then(async data => {
        // Check if this is first load or if we should update existing grid
        const shouldAnimate = previousData !== null && currentGridElement !== null && existingCard && !existingCard.classList.contains('sales-team-leaderboard-loading');

        if (shouldAnimate && previousData) {
            const dataChanged = hasDataChanged(previousData, data.salesTeams);

            if (dataChanged && !isAnimating) {
                // Prevent new updates while animating
                isAnimating = true;

                // Cancel any ongoing animations before starting new ones
                animationController.cancelAll();

                // Update existing grid with animations
                await updateGridWithAnimations(currentGridElement as HTMLElement, previousData, data.salesTeams);

                // Allow updates again
                isAnimating = false;
            }

            // Update last updated time
            const lastUpdatedElement = existingCard.querySelector('.last-updated') as HTMLElement;
            if (lastUpdatedElement) {
                lastUpdatedElement.innerText = data.lastUpdated.toLocaleString(DateTime.TIME_WITH_SECONDS);
            }

            // Stop refresh button spinning
            const refreshBtn = existingCard.querySelector('.refresh-btn i') as HTMLElement;
            if (refreshBtn) {
                refreshBtn.classList.remove('fa-spin');
            }
        } else if (!shouldAnimate) {
            // First load or full refresh needed
            const card = template.Clone('#sales-team-leaderboard');
            const grid = drawSalesTeamGrid(data.salesTeams);

            const container = card.querySelector('.sales-team-leaderboard-table') as HTMLElement;
            container.appendChild(grid);

            const lastUpdatedElement = card.querySelector('.last-updated') as HTMLElement;
            if (lastUpdatedElement) {
                lastUpdatedElement.innerText = data.lastUpdated.toLocaleString(DateTime.TIME_WITH_SECONDS);
            }

            // Add refresh button click handler
            const refreshBtn = card.querySelector('.refresh-btn') as HTMLButtonElement;
            if (refreshBtn) {
                refreshBtn.addEventListener('click', () => {
                    void SalesTeamLeaderboard(parentElement, true);
                });
            }

            // Add sort dropdown handlers
            const sortDropdownItems = card.querySelectorAll('.sort-dropdown .dropdown-item');
            const sortLabel = card.querySelector('.sort-label') as HTMLElement;

            sortDropdownItems.forEach(item => {
                item.addEventListener('click', e => {
                    e.preventDefault();
                    const target = e.currentTarget as HTMLAnchorElement;
                    const newSortMode = target.getAttribute('data-sort') as 'today' | 'quarter' | 'provisional';

                    if (newSortMode === sortMode) {
                        return; // Already in this mode
                    }

                    // Update dropdown item states
                    sortDropdownItems.forEach(i => i.classList.remove('active'));
                    target.classList.add('active');

                    // Update button label
                    if (sortLabel) {
                        if (newSortMode === 'today') {
                            sortLabel.textContent = i18n('today');
                        } else if (newSortMode === 'quarter') {
                            sortLabel.textContent = i18n('standings');
                        } else {
                            sortLabel.textContent = i18n('provisional');
                        }
                    }

                    // Animate sort transition
                    if (currentQuarterData && currentGridElement) {
                        const body = currentGridElement.querySelector('.leaderboard-body') as HTMLElement;
                        if (!body) {
                            return;
                        }

                        // Calculate old positions with current sort mode
                        const oldRows = processTeamData(currentQuarterData.salesTeams);

                        // Update sort mode
                        sortMode = newSortMode;

                        // Calculate new positions with new sort mode
                        const newRows = processTeamData(currentQuarterData.salesTeams);

                        // Build maps for row data lookup
                        const oldRowMap = new Map<string, row>();
                        const newRowMap = new Map<string, row>();
                        oldRows.forEach(row => oldRowMap.set(row.originalSalesTeamID, row));
                        newRows.forEach(row => newRowMap.set(row.originalSalesTeamID, row));

                        // Calculate max quarter points for crown icon
                        const maxQuarterPoints = Math.max(...newRows.map(r => r.quarterPoints), 0);

                        // Animate position changes
                        void animateRowPositions(body, oldRows, newRows).then(() => {
                            // After animation completes, update all row fields using unified function
                            const rows = body.querySelectorAll('.leaderboard-row');
                            rows.forEach(rowElement => {
                                const teamID = rowElement.getAttribute('data-team-id');
                                if (!teamID) {
                                    return;
                                }

                                const oldRowData = oldRowMap.get(teamID);
                                const newRowData = newRowMap.get(teamID);
                                if (!oldRowData || !newRowData) {
                                    return;
                                }

                                // Use unified updateRow function
                                void updateRow(rowElement as HTMLElement, oldRowData, newRowData, maxQuarterPoints);
                            });
                        });
                    }
                });
            });

            const oldCard = parentElement.querySelector('.sales-team-leaderboard-card');
            if (oldCard) {
                // Dispose tooltips on the card we're about to drop so instances don't leak.
                oldCard.querySelectorAll('[data-bs-toggle="tooltip"], .member-badge').forEach(el => {
                    bootstrap.Tooltip.getInstance(el)?.dispose();
                });
                parentElement.replaceChild(card, oldCard);
            } else {
                parentElement.appendChild(card);
            }

            // Store reference to current grid - need to get it from the DOM after it's added
            currentGridElement = parentElement.querySelector('.sales-team-leaderboard-table .leaderboard-grid') as HTMLElement;
        }

        // Store current data for next comparison
        previousData = data.salesTeams;
        currentQuarterData = data;
    }).catch((err: unknown) => {
        // Stop refresh button spinning on error
        const card = parentElement.querySelector('.sales-team-leaderboard-card');
        if (card) {
            const refreshBtn = card.querySelector('.refresh-btn i') as HTMLElement;
            if (refreshBtn) {
                refreshBtn.classList.remove('fa-spin');
            }
        }

        // Remove loading card on error only if it was first load
        if (!existingCard) {
            const loadingCard = parentElement.querySelector('.sales-team-leaderboard-card');
            if (loadingCard) {
                loadingCard.remove();
            }
        }

        // Re-throw error with detailed information for debugging
        if (IsAxiosError(err)) {
            console.error('Sales team leaderboard error details:', {
                error: err,
                response: err.response,
                status: err.response?.status,
                data: err.response?.data,
                message: err.message,
            });

            let errorMsg = 'Unknown error';
            const responseData = err.response?.data as { error?: string } | undefined;
            if (typeof responseData?.error === 'string') {
                errorMsg = responseData.error;
            } else if (err.message) {
                errorMsg = err.message;
            }
            throw new Error(`Failed to load sales team leaderboard: ${errorMsg}`);
        } else {
            console.error('Sales team leaderboard error:', err);
            const errorMsg = err instanceof Error ? err.message : 'Unknown error';
            throw new Error(`Failed to load sales team leaderboard: ${errorMsg}`);
        }
    });
}

function drawSalesTeamGrid(salesTeams: models.SalesTeamStats[]): HTMLElement {
    const container = template.Clone('#sales-team-leaderboard-grid').firstElementChild as HTMLElement;
    const body = container.querySelector('.leaderboard-body') as HTMLElement;

    const rows: row[] = [];
    let todayTotalTotal = Big(0);
    let pendingTotalTotal = Big(0);
    let lastWorkingDayTotalTotal = Big(0);

    // Build map of previous quarter positions for change calculation
    const previousQuarterPositions = new Map<string, number>();
    if (previousData) {
        for (const prevTeam of previousData) {
            if (prevTeam.quarterPosition) {
                previousQuarterPositions.set(prevTeam.salesTeamName, prevTeam.quarterPosition);
            }
        }
    }

    for (const st of salesTeams) {
        let ratio = Big(0);
        if (!Big(st.lastWorkingDayTotal).eq(0)) {
            ratio = Big(st.todayTotal).sub(st.lastWorkingDayTotal).div(st.lastWorkingDayTotal).mul(100);
        }

        const teamRow: row = {
            position: 0,
            positionChange: 0,
            todayPositionChange: 0,
            quarterPositionChange: 0,
            salesTeamID: st.salesTeamID,
            originalSalesTeamID: st.originalSalesTeamID,
            salesTeamName: st.salesTeamName,
            todayTotal: Big(st.todayTotal),
            pendingTotal: Big(st.pendingTotal || 0),
            lastWorkingDayTotal: Big(st.lastWorkingDayTotal),
            ratio,
            members: st.members,
            primaryRealmColor: st.primaryRealmColor,
            quarterPoints: st.quarterPoints || 0,
            wins: st.wins || 0,
            seconds: st.seconds || 0,
            thirds: st.thirds || 0,
            podiums: st.podiums || 0,
            quarterPosition: st.quarterPosition || 0,
        };

        rows.push(teamRow);
        todayTotalTotal = todayTotalTotal.add(st.todayTotal);
        pendingTotalTotal = pendingTotalTotal.add(st.pendingTotal || 0);
        lastWorkingDayTotalTotal = lastWorkingDayTotalTotal.add(st.lastWorkingDayTotal);
    }

    // Sort rows based on current mode
    const sorted = sortRows(rows, sortMode);

    // Calculate position changes based on last working day
    // Build map of last working day positions from API data
    const lastWorkingDayTodayPositionsMap = new Map<string, number>();
    const lastWorkingDayQuarterPositionsMap = new Map<string, number>();
    for (const st of salesTeams) {
        if (st.lastWorkingDayTodayPosition) {
            lastWorkingDayTodayPositionsMap.set(st.originalSalesTeamID, st.lastWorkingDayTodayPosition);
        }
        if (st.lastWorkingDayQuarterPosition) {
            lastWorkingDayQuarterPositionsMap.set(st.originalSalesTeamID, st.lastWorkingDayQuarterPosition);
        }
    }

    // Calculate position changes for each sorted row
    for (const row of sorted) {
        const lastTodayPos = lastWorkingDayTodayPositionsMap.get(row.originalSalesTeamID);
        const lastQuarterPos = lastWorkingDayQuarterPositionsMap.get(row.originalSalesTeamID);

        // Calculate today position change (positive = moved up)
        if (lastTodayPos) {
            row.todayPositionChange = lastTodayPos - row.position;
        }

        // Calculate quarter position change (positive = moved up)
        if (lastQuarterPos) {
            row.quarterPositionChange = lastQuarterPos - row.position;
        }

        // Set positionChange based on current sort mode
        row.positionChange = sortMode === 'quarter' ? row.quarterPositionChange : row.todayPositionChange;
    }

    // Find max quarter points for crown emoji
    const maxQuarterPoints = Math.max(...sorted.map(r => r.quarterPoints), 0);

    // Create grid rows
    for (const s of sorted) {
        const rowElement = createLeaderboardRow(s, maxQuarterPoints);
        body.appendChild(rowElement);
    }

    // Update footer totals
    (container.querySelector('.day-total') as HTMLElement).innerText = util.DollarFormat(todayTotalTotal, false, true);
    (container.querySelector('.pending-total') as HTMLElement).innerText = util.DollarFormat(pendingTotalTotal, false, true);

    // Header (i) tooltips — Bootstrap 5 is opt-in; markup alone does nothing.
    // Dispose-then-create so full redraws don't stack instances / warn.
    container.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
        bootstrap.Tooltip.getInstance(el)?.dispose();
        new bootstrap.Tooltip(el);
    });

    return container;
}

// ============================================================================
// Render Functions - Single source of truth for each cell type
// ============================================================================

function renderPositionCell(rowData: row): string {
    if (rowData.position === 1) {
        return '<i class="fas fa-medal" style="color: #FFD700; font-size: 1.2rem;"></i>';
    } else if (rowData.position === 2) {
        return '<i class="fas fa-medal" style="color: #C0C0C0; font-size: 1.2rem;"></i>';
    } else if (rowData.position === 3) {
        return '<i class="fas fa-medal" style="color: #CD7F32; font-size: 1.2rem;"></i>';
    }
    return `<span class="position-number">${rowData.position}</span>`;
}

function renderMedalsDisplay(rowData: row): string {
    const medalsParts: string[] = [];
    if (rowData.wins && rowData.wins > 0) {
        medalsParts.push(`<span class="medal gold">●</span> ${rowData.wins}`);
    }
    if (rowData.seconds && rowData.seconds > 0) {
        medalsParts.push(`<span class="medal silver">●</span> ${rowData.seconds}`);
    }
    if (rowData.thirds && rowData.thirds > 0) {
        medalsParts.push(`<span class="medal bronze">●</span> ${rowData.thirds}`);
    }
    // Always show badge background for consistency
    return medalsParts.length > 0 ? `<span class="medals-badge medals-badge-podium">${medalsParts.join(' ')}</span>` : '';
}

// Fills a .member-badges-container with the standardized user avatars (photo →
// initials in the per-name color → person icon), capped at three plus a "+N"
// chip. Each badge carries its own Bootstrap tooltip with the member's name.
function paintMemberBadges($container: HTMLElement, members: models.User[]): void {
    // Dispose tooltips on the badges we're about to replace so they don't leak.
    $container.querySelectorAll('.member-badge').forEach(el => bootstrap.Tooltip.getInstance(el)?.dispose());
    $container.replaceChildren();
    for (const member of members.slice(0, 3)) {
        // Members are real User instances — the avatar handles photo/initials.
        // No background override, so the initials fall back to the same stable
        // per-name color used on Friday and everywhere else.
        const $badge = member.avatar({
            size: 20,
            class: 'member-badge',
            title: '',
        });
        const name = member.fullName();
        if (name) {
            new bootstrap.Tooltip($badge, { title: name, trigger: 'hover' });
        }
        $container.appendChild($badge);
    }

    if (members.length > 3) {
        const $extra = document.createElement('span');
        $extra.classList.add('member-badge', 'extra');
        $extra.textContent = `+${members.length - 3}`;
        $container.appendChild($extra);
    }
}

function renderRevenueCell(rowData: row): string {
    return util.DollarFormat(rowData.todayTotal, false, true);
}

function renderPendingCell(rowData: row): string {
    return util.DollarFormat(rowData.pendingTotal, false, true);
}

function renderQuarterPointsCell(rowData: row, maxQuarterPoints: number): string {
    // Always show badge background for consistency
    const badgeClass = 'px-2 py-1 rounded';
    const badgeBg = 'background-color: var(--bs-body-bg);';
    const pointsTextStyle = 'color: var(--bs-body-color);';

    const isLeader = rowData.quarterPoints === maxQuarterPoints && maxQuarterPoints > 0;
    if (rowData.quarterPoints > 0) {
        const topLabel = i18n('leaderboardTopLabel');
        const topLabelAttr = topLabel ? ` title="${topLabel}"` : '';
        const crownIcon = isLeader ?
            `<i class="fas fa-crown" style="color: #FFD700; margin-right: 0.25rem;"${topLabelAttr}></i>` :
            '';
        return `<span class='fw-semibold ${badgeClass}' style='${badgeBg} ${pointsTextStyle}'>${crownIcon}${rowData.quarterPoints.toLocaleString()}</span>`;
    }
    return `<span class="text-muted opacity-50 ${badgeClass}" style='${badgeBg} ${pointsTextStyle}'>—</span>`;
}

function renderPositionChangeCell(rowData: row): string {
    // Always show badge background for consistency
    const badgeClass = 'px-2 py-1 rounded';
    const badgeBg = 'background-color: var(--bs-body-bg);';

    if (rowData.positionChange > 0) {
        return `<span class='text-success fw-semibold ${badgeClass}' style='${badgeBg}'><i class='fas fa-caret-up'></i> ${rowData.positionChange}</span>`;
    } else if (rowData.positionChange < 0) {
        return `<span class='text-danger fw-semibold ${badgeClass}' style='${badgeBg}'><i class='fas fa-caret-down'></i> ${Math.abs(rowData.positionChange)}</span>`;
    }
    return `<span class="text-muted opacity-50 ${badgeClass}" style='${badgeBg}'>—</span>`;
}

function createLeaderboardRow(s: row, maxQuarterPoints: number): HTMLElement {
    // Use render functions for all cell content
    const positionDisplay = renderPositionCell(s);
    const medalsDisplay = renderMedalsDisplay(s);
    const revenueDisplay = renderRevenueCell(s);
    const pendingDisplay = renderPendingCell(s);
    const quarterPointsDisplay = renderQuarterPointsCell(s, maxQuarterPoints);
    const positionChangeIcon = renderPositionChangeCell(s);

    const rowDiv = document.createElement('div');
    const podium = s.position <= 3;
    rowDiv.className = `leaderboard-row row g-0 align-items-center ${podium ? 'top-performer' : ''}`;
    rowDiv.setAttribute('data-team-id', s.originalSalesTeamID);

    // Apply realm color based on position and determine text styling
    let teamIconStyle = 'opacity: 0.6; font-size: 0.9rem;';

    if (s.primaryRealmColor && s.position <= 3) {
        const realmColor = s.primaryRealmColor;

        if (s.position === 1) {
            // First place: full color background with text color based on background
            const isDark = color.IsDark(realmColor);
            rowDiv.style.backgroundColor = `#${realmColor}`;
            rowDiv.style.color = isDark ? 'white' : 'black';
            // For light backgrounds, make icon darker
            if (!isDark) {
                teamIconStyle = 'opacity: 1; font-size: 0.9rem; color: rgba(0, 0, 0, 0.7);';
            }
        } else if (s.position === 2) {
            // Second place: 20% opacity, use default body color
            rowDiv.style.backgroundColor = `#${realmColor}33`; // 33 hex = 20% opacity
        } else if (s.position === 3) {
            // Third place: 10% opacity, use default body color
            rowDiv.style.backgroundColor = `#${realmColor}1A`; // 1A hex ≈ 10% opacity
        }
    }

    rowDiv.innerHTML = `
        <div class="col-1 text-center position-cell">
            ${positionDisplay}
        </div>
        <div class="col-4 team-cell">
            <div class="team-info">
                <div class="team-name">
                    <i class="fas fa-users fa-fw me-1" style="${teamIconStyle}"></i>
                    <span class="team-name-text">${s.salesTeamName}</span>
                    ${medalsDisplay}
                </div>
                <div class="member-badges-container"></div>
            </div>
        </div>
        <div class="col-2 text-center revenue-cell fw-bold">
            ${revenueDisplay}
        </div>
        <div class="col-2 text-center pending-cell ${s.position <= 3 ? '' : 'text-muted'}">
            ${pendingDisplay}
        </div>
        <div class="col-2 text-center quarter-pts-cell">
            ${quarterPointsDisplay}
        </div>
        <div class="col-1 text-center change-cell">${positionChangeIcon}</div>
    `;

    // Each badge carries its own name tooltip (set in paintMemberBadges), so
    // the container no longer needs a combined "Team Members" tooltip.
    const memberContainer = rowDiv.querySelector('.member-badges-container') as HTMLElement;
    paintMemberBadges(memberContainer, s.members);

    return rowDiv;
}

// Check if data has changed
function hasDataChanged(oldData: models.SalesTeamStats[], newData: models.SalesTeamStats[]): boolean {
    if (oldData.length !== newData.length) {
        return true;
    }

    for (const oldTeam of oldData) {
        const newTeam = newData.find(t => t.originalSalesTeamID === oldTeam.originalSalesTeamID);

        if (!newTeam) {
            return true;
        }

        // Direct string comparison since they're already strings
        if (!oldTeam.todayTotal.eq(newTeam.todayTotal) ||
            !oldTeam.pendingTotal.eq(newTeam.pendingTotal) ||
            !oldTeam.lastWorkingDayTotal.eq(newTeam.lastWorkingDayTotal) ||
            oldTeam.members.length !== newTeam.members.length) {
            return true;
        }
    }

    return false;
}

// ============================================================================
// Unified Row Update Function - Updates all fields with animations
// ============================================================================

async function updateRow(
    rowElement: HTMLElement,
    oldRowData: row,
    newRowData: row,
    maxQuarterPoints: number,
): Promise<void> {
    const animations: Promise<void>[] = [];

    // 1. Position cell - update if changed
    if (oldRowData.position !== newRowData.position) {
        const positionCell = rowElement.querySelector('.position-cell') as HTMLElement;
        if (positionCell) {
            positionCell.innerHTML = renderPositionCell(newRowData);
        }
    }

    // 2. Medals display - update if wins/seconds/thirds changed
    if (oldRowData.wins !== newRowData.wins ||
        oldRowData.seconds !== newRowData.seconds ||
        oldRowData.thirds !== newRowData.thirds) {
        const teamNameDiv = rowElement.querySelector('.team-name') as HTMLElement;
        if (teamNameDiv) {
            // Find existing medals badge or add it after team name
            const medalsSpan = teamNameDiv.querySelector('.medals-badge');
            const newMedalsHTML = renderMedalsDisplay(newRowData);
            if (medalsSpan) {
                if (newMedalsHTML) {
                    medalsSpan.outerHTML = newMedalsHTML;
                } else {
                    medalsSpan.remove();
                }
            } else if (newMedalsHTML) {
                teamNameDiv.insertAdjacentHTML('beforeend', newMedalsHTML);
            }
        }
    }

    // 3. Member badges - update if members changed
    // Check if member count or member list changed
    const membersChanged = oldRowData.members.length !== newRowData.members.length ||
        oldRowData.members.some((m, i) => m.id !== newRowData.members[i]?.id);
    if (membersChanged) {
        const memberContainer = rowElement.querySelector('.member-badges-container') as HTMLElement;
        if (memberContainer) {
            // paintMemberBadges rebuilds the badges and their per-name tooltips.
            paintMemberBadges(memberContainer, newRowData.members);
        }
    }

    // 4. Revenue cell - animate if changed
    const todayCell = rowElement.querySelector('.revenue-cell') as HTMLElement;
    if (todayCell && !oldRowData.todayTotal.eq(newRowData.todayTotal)) {
        const animation = new MoneyAnimation(todayCell, oldRowData.todayTotal, newRowData.todayTotal);
        animations.push(animation.animate());
    }

    // 5. Pending cell - animate if changed
    const pendingCell = rowElement.querySelector('.pending-cell') as HTMLElement;
    if (pendingCell && !oldRowData.pendingTotal.eq(newRowData.pendingTotal)) {
        const animation = new MoneyAnimation(pendingCell, oldRowData.pendingTotal, newRowData.pendingTotal);
        animations.push(animation.animate());
    }

    // 6. Quarter points cell - animate if changed
    const quarterPtsCell = rowElement.querySelector('.quarter-pts-cell') as HTMLElement;
    if (quarterPtsCell && oldRowData.quarterPoints !== newRowData.quarterPoints) {
        // For now, just redraw (could animate in future)
        quarterPtsCell.innerHTML = renderQuarterPointsCell(newRowData, maxQuarterPoints);
    }

    // 7. Position change cell - update if changed
    const changeCell = rowElement.querySelector('.change-cell') as HTMLElement;
    if (changeCell && oldRowData.positionChange !== newRowData.positionChange) {
        changeCell.innerHTML = renderPositionChangeCell(newRowData);
    }

    // 8. Row styling - ALWAYS update to ensure consistency
    // This includes background color, text color, and position-dependent classes
    applyRowStyling(rowElement, newRowData);

    // 9. Update pending cell classes based on position
    if (pendingCell) {
        if (newRowData.position <= 3) {
            pendingCell.classList.remove('text-muted');
        } else {
            pendingCell.classList.add('text-muted');
        }
    }

    // Wait for all animations to complete
    await Promise.all(animations);
}

// ============================================================================
// Styling Functions
// ============================================================================

function applyRowStyling(rowElement: HTMLElement, rowData: row): void {
    // Update top-performer class
    rowElement.classList.toggle('top-performer', rowData.position <= 3);

    // Reset styles first
    rowElement.style.backgroundColor = '';
    rowElement.style.color = '';

    const teamIcon = rowElement.querySelector('.fa-users') as HTMLElement;
    if (teamIcon) {
        teamIcon.style.opacity = '0.6';
        teamIcon.style.color = '';
    }

    // Apply realm color based on position
    if (rowData.primaryRealmColor && rowData.position <= 3) {
        const realmColor = rowData.primaryRealmColor;

        if (rowData.position === 1) {
            // First place: full color background with text color based on background
            const isDark = color.IsDark(realmColor);
            rowElement.style.backgroundColor = `#${realmColor}`;
            rowElement.style.color = isDark ? 'white' : 'black';
            // For light backgrounds, make icon darker
            if (!isDark && teamIcon) {
                teamIcon.style.opacity = '1';
                teamIcon.style.color = 'rgba(0, 0, 0, 0.7)';
            }
        } else if (rowData.position === 2) {
            // Second place: 20% opacity, use default body color
            rowElement.style.backgroundColor = `#${realmColor}33`; // 33 hex = 20% opacity
        } else if (rowData.position === 3) {
            // Third place: 10% opacity, use default body color
            rowElement.style.backgroundColor = `#${realmColor}1A`; // 1A hex ≈ 10% opacity
        }
    }
}

// Update grid with animations
async function updateGridWithAnimations(container: HTMLElement, oldData: models.SalesTeamStats[], newData: models.SalesTeamStats[]): Promise<void> {
    const body = container.querySelector('.leaderboard-body') as HTMLElement;
    if (!body) {
        return;
    }

    // Calculate old and new positions
    const oldRows = processTeamData(oldData);
    const newRows = processTeamData(newData);

    // Create maps for easy lookup
    const oldRowMap = new Map<string, row>();
    const newRowMap = new Map<string, row>();

    oldRows.forEach(row => {
        oldRowMap.set(row.originalSalesTeamID, row);
    });

    newRows.forEach(row => {
        newRowMap.set(row.originalSalesTeamID, row);
    });

    // Calculate max quarter points for crown icon
    const maxQuarterPoints = Math.max(...newRows.map(r => r.quarterPoints), 0);

    // Update each row using unified updateRow function
    const rowUpdatePromises: Promise<void>[] = [];
    const rows = body.querySelectorAll('.leaderboard-row');

    rows.forEach(rowElement => {
        const teamID = rowElement.getAttribute('data-team-id');
        if (!teamID) {
            return;
        }

        const oldRowData = oldRowMap.get(teamID);
        const newRowData = newRowMap.get(teamID);

        if (!oldRowData || !newRowData) {
            return;
        }

        // Use unified updateRow function
        const updatePromise = updateRow(rowElement as HTMLElement, oldRowData, newRowData, maxQuarterPoints);
        rowUpdatePromises.push(updatePromise);
    });

    // Animate row position changes with smooth transitions
    await animateRowPositions(body, oldRows, newRows);

    // Update footer totals with animation
    const footerAnimations = updateFooterTotals(container, oldData, newData);

    // Wait for all animations to complete (row updates + footer updates)
    await Promise.all([...rowUpdatePromises, ...footerAnimations]);
}

// Process team data into sorted rows
function processTeamData(salesTeams: models.SalesTeamStats[]): row[] {
    const rows: row[] = [];

    for (const st of salesTeams) {
        let ratio = Big(0);
        if (!Big(st.lastWorkingDayTotal).eq(0)) {
            ratio = Big(st.todayTotal).sub(st.lastWorkingDayTotal).div(st.lastWorkingDayTotal).mul(100);
        }

        const teamRow: row = {
            position: 0,
            positionChange: 0,
            todayPositionChange: 0,
            quarterPositionChange: 0,
            salesTeamID: st.salesTeamID,
            originalSalesTeamID: st.originalSalesTeamID,
            salesTeamName: st.salesTeamName,
            todayTotal: Big(st.todayTotal),
            pendingTotal: Big(st.pendingTotal || 0),
            lastWorkingDayTotal: Big(st.lastWorkingDayTotal),
            ratio,
            members: st.members,
            primaryRealmColor: st.primaryRealmColor,
            quarterPoints: st.quarterPoints || 0,
            wins: st.wins || 0,
            seconds: st.seconds || 0,
            thirds: st.thirds || 0,
            podiums: st.podiums || 0,
            quarterPosition: st.quarterPosition || 0,
        };

        rows.push(teamRow);
    }

    // Sort rows based on current mode
    const sorted = sortRows(rows, sortMode);

    // Calculate position changes based on last working day
    // Build map of last working day positions from API data
    const lastWorkingDayTodayPositionsMap = new Map<string, number>();
    const lastWorkingDayQuarterPositionsMap = new Map<string, number>();
    for (const st of salesTeams) {
        if (st.lastWorkingDayTodayPosition) {
            lastWorkingDayTodayPositionsMap.set(st.originalSalesTeamID, st.lastWorkingDayTodayPosition);
        }
        if (st.lastWorkingDayQuarterPosition) {
            lastWorkingDayQuarterPositionsMap.set(st.originalSalesTeamID, st.lastWorkingDayQuarterPosition);
        }
    }

    // Calculate position changes for each sorted row
    for (const row of sorted) {
        const lastTodayPos = lastWorkingDayTodayPositionsMap.get(row.originalSalesTeamID);
        const lastQuarterPos = lastWorkingDayQuarterPositionsMap.get(row.originalSalesTeamID);

        // Calculate today position change (positive = moved up)
        if (lastTodayPos) {
            row.todayPositionChange = lastTodayPos - row.position;
        }

        // Calculate quarter position change (positive = moved up)
        if (lastQuarterPos) {
            row.quarterPositionChange = lastQuarterPos - row.position;
        }

        // Set positionChange based on current sort mode
        row.positionChange = sortMode === 'quarter' ? row.quarterPositionChange : row.todayPositionChange;
    }

    return sorted;
}

// Smooth row position animations using absolute positioning temporarily
async function animateRowPositions(body: HTMLElement, oldRows: row[], newRows: row[]): Promise<void> {
    const rowElements = Array.from(body.querySelectorAll('.leaderboard-row')) as HTMLElement[];

    // Build position maps
    const oldPositions = new Map<string, number>();
    const newPositions = new Map<string, number>();

    oldRows.forEach((row, index) => {
        oldPositions.set(row.originalSalesTeamID, index);
    });

    newRows.forEach((row, index) => {
        newPositions.set(row.originalSalesTeamID, index);
    });

    // Check if any positions actually changed
    let hasChanges = false;
    for (const [teamID, oldPos] of oldPositions) {
        const newPos = newPositions.get(teamID);
        if (newPos !== undefined && newPos !== oldPos) {
            hasChanges = true;
            break;
        }
    }

    if (!hasChanges) {
        return;
    }

    // Get initial positions
    const initialPositions = new Map<string, DOMRect>();
    rowElements.forEach(el => {
        const teamID = el.getAttribute('data-team-id');
        if (teamID) {
            initialPositions.set(teamID, el.getBoundingClientRect());
        }
    });

    // Create new sorted order
    const sortedElements: HTMLElement[] = [];
    newRows.forEach(row => {
        const element = rowElements.find(el => el.getAttribute('data-team-id') === row.originalSalesTeamID);
        if (element) {
            sortedElements.push(element);
        }
    });

    // Clear and re-append in new order
    body.innerHTML = '';
    sortedElements.forEach(el => body.appendChild(el));

    // Get final positions
    const finalPositions = new Map<string, DOMRect>();
    sortedElements.forEach(el => {
        const teamID = el.getAttribute('data-team-id');
        if (teamID) {
            finalPositions.set(teamID, el.getBoundingClientRect());
        }
    });

    // Apply animations
    const animatingElements: HTMLElement[] = [];
    sortedElements.forEach(el => {
        const teamID = el.getAttribute('data-team-id');
        if (!teamID) {
            return;
        }

        const initial = initialPositions.get(teamID);
        const final = finalPositions.get(teamID);

        if (initial && final) {
            const deltaY = initial.top - final.top;

            if (Math.abs(deltaY) > 1) {
                // Set initial position
                el.style.transform = `translateY(${deltaY}px)`;
                el.style.transition = 'none';
                animatingElements.push(el);
            }
        }
    });

    // Force reflow
    body.getBoundingClientRect();

    // Animate to final positions
    animatingElements.forEach(el => {
        el.style.transform = '';
        el.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    });

    // Clean up after animation
    await new Promise(resolve => setTimeout(resolve, 500));
    animatingElements.forEach(el => {
        el.style.transform = '';
        el.style.transition = '';
    });
}

// Update footer totals with animations
function updateFooterTotals(container: HTMLElement, oldData: models.SalesTeamStats[], newData: models.SalesTeamStats[]): Promise<void>[] {
    const animations: Promise<void>[] = [];

    let oldTodayTotal = Big(0);
    let oldPendingTotal = Big(0);
    let oldLastWorkingDayTotal = Big(0);
    let newTodayTotal = Big(0);
    let newPendingTotal = Big(0);
    let newLastWorkingDayTotal = Big(0);

    oldData.forEach(team => {
        oldTodayTotal = oldTodayTotal.add(team.todayTotal);
        oldPendingTotal = oldPendingTotal.add(team.pendingTotal || 0);
        oldLastWorkingDayTotal = oldLastWorkingDayTotal.add(team.lastWorkingDayTotal);
    });

    newData.forEach(team => {
        newTodayTotal = newTodayTotal.add(team.todayTotal);
        newPendingTotal = newPendingTotal.add(team.pendingTotal || 0);
        newLastWorkingDayTotal = newLastWorkingDayTotal.add(team.lastWorkingDayTotal);
    });

    // Animate today total
    const dayTotalElement = container.querySelector('.day-total') as HTMLElement;
    if (dayTotalElement && !oldTodayTotal.eq(newTodayTotal)) {
        const animation = new MoneyAnimation(dayTotalElement, oldTodayTotal, newTodayTotal);
        animations.push(animation.animate());
    }

    // Animate pending total
    const pendingTotalElement = container.querySelector('.pending-total') as HTMLElement;
    if (pendingTotalElement && !oldPendingTotal.eq(newPendingTotal)) {
        const animation = new MoneyAnimation(pendingTotalElement, oldPendingTotal, newPendingTotal);
        animations.push(animation.animate());
    }

    return animations;
}